import { filterExistsAndExcludes, ModeEnum, type CustomerUser, type UseFormTableProps } from '@talos/kyoko';
import { intersection } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';

export type CustomerUsersModeFilter = 'All' | 'Enabled' | 'Disabled';

export interface CustomerUsersTableFilter {
  Counterparties?: string[];
  Roles?: string[];
}

export interface CustomerUsersExternallyControlledFilter {
  keys: readonly string[];
  values: Partial<CustomerUsersTableFilter>;
}

interface UseCustomerUsersTableFiltersOptions {
  externallyControlledFilter?: CustomerUsersExternallyControlledFilter;
}

function getUserRoleNames(user: CustomerUser): string[] {
  return (user.Roles ?? []).map(role => (typeof role === 'string' ? role : role.Name));
}

function hasSelections(selections: string[] | undefined) {
  return selections != null && selections.length > 0;
}

function isCustomerUsersFilterKey(key: string): key is keyof CustomerUsersTableFilter {
  return key === 'Counterparties' || key === 'Roles';
}

function countControlledFilters(externallyControlledFilter: CustomerUsersExternallyControlledFilter | undefined) {
  return (
    externallyControlledFilter?.keys.filter(
      (key): key is keyof CustomerUsersTableFilter =>
        isCustomerUsersFilterKey(key) && hasSelections(externallyControlledFilter.values[key])
    ).length ?? 0
  );
}

export function useCustomerUsersTableFilters({ externallyControlledFilter }: UseCustomerUsersTableFiltersOptions = {}) {
  const [modeFilter, setModeFilter] = useState<CustomerUsersModeFilter>('Enabled');
  const [filter, setFilter] = useState<CustomerUsersTableFilter>({});
  const isCustomerFilterExternallyControlled = externallyControlledFilter?.keys.includes('Counterparties') === true;
  const effectiveFilter = useMemo(
    () => ({
      ...filter,
      ...externallyControlledFilter?.values,
    }),
    [externallyControlledFilter?.values, filter]
  );

  const activeLocalFilterCount =
    (modeFilter === 'Enabled' ? 0 : 1) +
    (isCustomerFilterExternallyControlled ? 0 : Number(hasSelections(filter.Counterparties))) +
    Number(hasSelections(filter.Roles));

  const activeFilterCount = activeLocalFilterCount + countControlledFilters(externallyControlledFilter);

  const resetFilters = useCallback(() => {
    setModeFilter('Enabled');
    setFilter({});
  }, []);

  const clientLocalFilter = useCallback<NonNullable<UseFormTableProps<CustomerUser>['clientLocalFilter']>>(
    node => {
      const data = node.data;
      if (data == null) {
        return false;
      }

      if (modeFilter === 'Enabled' && data.Mode !== ModeEnum.Enabled) {
        return false;
      }
      if (modeFilter === 'Disabled' && data.Mode !== ModeEnum.Disabled) {
        return false;
      }

      if (filterExistsAndExcludes(effectiveFilter, 'Counterparties', data, 'Counterparty')) {
        return false;
      }

      if (effectiveFilter.Roles != null && effectiveFilter.Roles.length > 0) {
        if (intersection(getUserRoleNames(data), effectiveFilter.Roles).length === 0) {
          return false;
        }
      }

      return true;
    },
    [effectiveFilter, modeFilter]
  );

  return {
    modeFilter,
    setModeFilter,
    filter,
    effectiveFilter,
    changeFilter: setFilter,
    clientLocalFilter,
    isCustomerFilterExternallyControlled,
    activeLocalFilterCount,
    activeFilterCount,
    resetFilters,
  };
}
